import { useState } from "react";
import { Link } from "react-router";
import { useDataProvider, useGetIdentity, useRefresh } from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useCharger, api } from "./charger";
import { dateHeure, euros } from "./api";

/* Fiche contact (19/10/2026) : ce que le prospect a fait sur le simulateur, son lien, ses mails. */
export function PanneauContact({ contactId, email }: { contactId: number | string; email?: string }) {
  const sim = useCharger<{ lien: string; prospect: any }>(`/api/simulateur/contact/${contactId}`);
  const mails = useCharger<{ mails: any[] }>(email ? `/api/crm/boite?dossier=recus&q=${encodeURIComponent(email)}` : null);
  const envoyes = useCharger<{ mails: any[] }>(email ? `/api/crm/boite?dossier=envoyes&q=${encodeURIComponent(email)}` : null);
  const [copie, setCopie] = useState(false);
  const [resume, setResume] = useState<string | null>(null);
  const [iaCours, setIaCours] = useState(false);
  const [appel, setAppel] = useState<{ resultat: string; note: string } | null>(null);
  const [appelOk, setAppelOk] = useState(false);
  const dataProvider = useDataProvider();
  const { data: identite } = useGetIdentity();
  const rafraichir = useRefresh();
  const enregistrerAppel = async (a: { resultat: string; note: string }) => {
    const texte = `Appel (${a.resultat})${a.note.trim() ? ` : ${a.note.trim()}` : ""}`;
    const { data: c } = await dataProvider.getOne("contacts", { id: contactId });
    const statut = a.resultat === "interesse" ? "hot" : c.status || "cold";
    await dataProvider.create("contact_notes", { data: { contact_id: contactId, text: texte, date: new Date().toISOString(), sales_id: identite?.id, status: statut } });
    await dataProvider.update("contacts", { id: contactId, data: { ...c, status: statut, last_seen: new Date().toISOString() }, previousData: c });
    await api.post(`/api/simulateur/contact/${contactId}/appel`, { ...a, noteFaite: true });
    rafraichir();
  };
  const p = sim.data?.prospect;
  const fiche = p?.fiche ? (typeof p.fiche === "string" ? JSON.parse(p.fiche) : p.fiche) : null;
  const tous = [...(mails.data?.mails || []), ...(envoyes.data?.mails || [])].sort((a, b) => String(b.recu_le).localeCompare(String(a.recu_le))).slice(0, 8);
  const ecrire = `/boite?p=${p?.id || ""}${email ? `&a=${encodeURIComponent(email)}` : ""}`;
  return (
    <Card className="mt-4">
      <CardContent className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="mr-auto text-lg font-semibold">Simulateur et mails</h3>
          {sim.data?.lien && (
            <Button variant="outline" size="sm" onClick={() => navigator.clipboard.writeText(sim.data!.lien).then(() => setCopie(true))}>
              {copie ? "Lien copié" : "Copier le lien du simulateur"}
            </Button>
          )}
          <Button variant="outline" size="sm" disabled={iaCours} onClick={async () => {
            setIaCours(true);
            try { setResume((await api.post<{ texte: string }>("/api/ia/resume", { contactId })).texte); } catch (e: any) { setResume(e.message); }
            setIaCours(false);
          }}>{iaCours ? "Analyse…" : "Résumé et prochaine action (IA)"}</Button>
          <Button size="sm" variant="outline" onClick={() => { setAppel({ resultat: "joint", note: "" }); setAppelOk(false); }}>Appel passé</Button>
          <Button size="sm" asChild><Link to={ecrire}>Écrire un mail</Link></Button>
        </div>
        {appel && (
          <div className="space-y-2 rounded-md border p-3">
            <div className="flex flex-wrap gap-2">
              {[["joint", "Joint"], ["interesse", "Intéressé"], ["rappeler", "À rappeler"], ["messagerie", "Messagerie"], ["pas-de-reponse", "Pas de réponse"], ["pas-interesse", "Pas intéressé"]].map(([v, l]) => (
                <Button key={v} size="sm" variant={appel.resultat === v ? "default" : "outline"} onClick={() => setAppel({ ...appel, resultat: v })}>{l}</Button>
              ))}
            </div>
            <textarea className="w-full rounded-md border p-2 text-sm" rows={3} placeholder="Ce qui s'est dit, l'objection entendue, ce qui a été convenu (facultatif)" value={appel.note} onChange={(e) => setAppel({ ...appel, note: e.target.value })} />
            <div className="flex gap-2">
              <Button size="sm" onClick={async () => { await enregistrerAppel(appel); setAppel(null); setAppelOk(true); }}>Enregistrer l'appel</Button>
              <Button size="sm" variant="outline" onClick={() => setAppel(null)}>Annuler</Button>
            </div>
          </div>
        )}
        {appelOk && <p className="text-green-800">Appel enregistré : la note est dans l'historique de la fiche.</p>}
        {resume && <pre className="whitespace-pre-wrap rounded-md border bg-muted p-3 font-sans text-sm">{resume}</pre>}
        {sim.erreur && <p className="text-red-700">{sim.erreur}</p>}
        {p && (
          <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
            <p>Accès demandé : <strong>{p.acces_demande_le ? dateHeure(p.acces_demande_le) : "non"}</strong></p>
            <p>Résultat vu : <strong>{p.resultat_le ? dateHeure(p.resultat_le) : "non"}</strong></p>
            {p.resultat_le && <p>Récupérable : <strong>{euros(p.gain_bas)} à {euros(p.gain_haut)} par an</strong></p>}
            {p.tache_principale && <p>À automatiser en premier : <strong>{p.tache_principale}</strong></p>}
            {p.activite && <p className="md:col-span-2">Activité : {p.activite}</p>}
            {p.rappel_tel && <p className="md:col-span-2">Rappel demandé : <strong>{p.rappel_tel}</strong> {p.rappel_creneau ? `(${p.rappel_creneau})` : ""}</p>}
            {p.ecrit_debut && <p className="md:col-span-2">Ce qu'il a écrit : « {p.ecrit_debut} »</p>}
          </div>
        )}
        {Array.isArray(p?.automatisables) && p.automatisables.length > 0 && (
          <div>
            <p className="font-semibold">Tout ce qui s'automatise chez lui</p>
            {p.automatisables.map((g: any) => (
              <div key={g.categorie} className="mt-1">
                <p className="text-xs font-semibold uppercase text-muted-foreground">{g.categorie}</p>
                <ul className="ml-4 list-disc">
                  {g.taches.map((t: any) => <li key={t.libelle}>{t.libelle}{t.automatisation ? ` · ${t.automatisation}` : ""} · {euros(t.gainAnBas)} à {euros(t.gainAnHaut)} par an</li>)}
                </ul>
              </div>
            ))}
          </div>
        )}
        {fiche && (
          <details>
            <summary className="cursor-pointer font-semibold">Fiche d'appel (préparée par l'analyse)</summary>
            <pre className="mt-2 whitespace-pre-wrap font-sans text-xs">{JSON.stringify(fiche, null, 2).replace(/[{}"\[\],]/g, "").replace(/\n\s*\n/g, "\n")}</pre>
          </details>
        )}
        {p && !p.acces_demande_le && <p className="text-muted-foreground">Pas encore passé par le simulateur : copiez son lien et envoyez-le lui pendant l'appel.</p>}
        {email && (
          <div>
            <p className="font-semibold">Derniers mails</p>
            {tous.length === 0 ? <p className="text-muted-foreground">Aucun mail échangé.</p> : (
              <ul className="space-y-1">
                {tous.map((m) => (
                  <li key={m.id}><Link className="underline" to={`/boite?m=${m.id}`}>{m.sens === "recu" ? "Reçu" : "Envoyé"} le {dateHeure(m.recu_le)} : {m.sujet}</Link></li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
